// CSV of COGS and gross margin per sale, with the per-line cost detail behind
// them. The Sage export is a VAT audit-trail posting format with no cost column,
// so this is the export that carries profitability.

import { orderCostSummary, lineMargin, isCarriage } from '@/lib/costMargin';

const esc = (v) => {
  const s = String(v ?? '');
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
};

const money = (v) => (v == null ? '' : Number(v).toFixed(2));
const pct = (v) => (v == null ? '' : v.toFixed(1));

/** How the cost on a sale was arrived at — shown on every row of the export. */
function costBasis(summary) {
  if (summary.cogs == null) return 'No cost recorded';
  if (summary.backfilled) return 'Backfilled estimate';
  if (summary.partial) return 'Partial — some lines have no cost';
  return 'Snapshot at sale';
}

const HEADER = [
  'Order Ref', 'Date', 'Customer', 'Source', 'Status',
  'Product Code', 'Description', 'Qty',
  'Line Net (ex VAT)', 'Unit Cost (ex VAT)', 'Line Cost', 'Line Margin', 'Line Margin %',
  'Order Net (ex VAT)', 'Order COGS', 'Order Margin', 'Order Margin %', 'Cost Basis',
];

/**
 * Builds and downloads the margin CSV. Returns the number of rows written.
 * One row per goods line, with the sale's own totals repeated alongside so the
 * figures reconcile without a pivot.
 */
export function exportOrderMarginsCsv(orders) {
  const rows = [];

  for (const order of orders || []) {
    const summary = orderCostSummary(order);
    const lines = (Array.isArray(order.line_items) ? order.line_items : []).filter((li) => !isCarriage(li));

    const base = [
      order.invoice_number || `#${String(order.id || '').slice(-8).toUpperCase()}`,
      order.created_date ? new Date(order.created_date).toLocaleDateString('en-GB') : '',
      order.customer_name || '',
      order.source || '',
      order.status || '',
    ];

    const totals = [
      money(summary.net),
      summary.cogs == null ? '' : money(summary.cogs),
      summary.margin == null ? '' : money(summary.margin),
      pct(summary.marginPct),
      costBasis(summary),
    ];

    if (lines.length === 0) {
      // No per-line breakdown (a booking, or a legacy record) — one summary row.
      rows.push([...base, '', order.product_name || 'Sale', order.quantity || 1,
        '', '', '', '', '', ...totals]);
      continue;
    }

    for (const li of lines) {
      const m = lineMargin(li);
      rows.push([
        ...base,
        li.product_code || '',
        li.description || '',
        li.qty ?? '',
        money(m.net),
        money(li.unit_cost_ex_vat),
        money(m.cost),
        money(m.margin),
        m.margin == null || m.net <= 0 ? '' : pct((m.margin / m.net) * 100),
        ...totals,
      ]);
    }
  }

  const csv = HEADER.map(esc).join(',') + '\n' + rows.map((r) => r.map(esc).join(',')).join('\n') + '\n';
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `order-margins-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);

  return rows.length;
}